<template>
  <div>
    <Head title="Cierres de Caja" />

    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <h1 class="text-3xl font-bold">Cierres de Caja</h1>
      </div>

      <div class="flex items-center gap-3">
        <Link
          class="px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors text-sm"
          href="/cash-registers/daily-report"
        >
          <i class="fa fa-chart-pie"></i>
          <span>Reporte Diario Consolidado</span>
        </Link>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm mb-6 p-6">
      <div class="grid grid-cols-1 gap-4" :class="(organizations && organizations.length > 1) ? 'md:grid-cols-4' : 'md:grid-cols-3'">
        <div v-if="organizations && organizations.length > 1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Sucursal / Tienda</label>
          <select
            v-model="filters.organization_id"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          >
            <option value="">Todas las sucursales</option>
            <option
              v-for="org in organizations"
              :key="org.id"
              :value="org.id"
            >
              {{ org.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Caja</label>
          <select
            v-model="filters.cash_register_id"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          >
            <option value="">Todas las cajas</option>
            <option
              v-for="register in cashRegisters"
              :key="register.id"
              :value="register.id"
            >
              {{ register.name }} {{ register.organization ? `(${register.organization.name})` : '' }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
          <input
            v-model="filters.date_from"
            type="date"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
          <input
            v-model="filters.date_to"
            type="date"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>
      </div>
    </div>

    <!-- Closures Table -->
    <div class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-center bg-gray-50">
            <th class="p-3 font-semibold">Fecha</th>
            <th class="p-3 font-semibold">Caja</th>
            <th class="p-3 font-semibold">Saldo Inicial</th>
            <th class="p-3 font-semibold">Saldo Esperado</th>
            <th class="p-3 font-semibold">Saldo Real</th>
            <th class="p-3 font-semibold">Diferencia</th>
            <th class="p-3 font-semibold">Cerrado por</th>
            <th class="p-3 font-semibold">Acciones</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="closure in closures.data"
            :key="closure.id"
            class="border-t border-gray-100 hover:bg-gray-50 transition-colors text-center"
          >
            <td class="p-3 text-gray-700 text-xs text-center">
              <div class="font-bold">{{ formatDate(closure.closure_date) }}</div>
              <span v-if="(closure.notes || '').includes('[ARQUEO PARCIAL]')" class="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-extrabold bg-indigo-100 text-indigo-800 border border-indigo-200">
                Arqueo Parcial
              </span>
              <span v-else class="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-extrabold bg-teal-100 text-teal-800 border border-teal-200">
                Cierre Definitivo
              </span>
            </td>

            <td class="p-3 text-gray-800 text-xs font-medium">
              <div class="font-bold">{{ closure.cashRegister?.name || closure.cash_register?.name || ('Caja #' + (closure.cash_register_id || '')) }}</div>
              <span v-if="closure.cashRegister?.organization || closure.organization" class="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                <i class="fa fa-store text-slate-400"></i> {{ (closure.cashRegister?.organization || closure.organization)?.name }}
              </span>
            </td>

            <td class="p-3 text-xs text-center">
              <div class="font-bold text-gray-900">${{ formatCurrency(closure.opening_balance) }}</div>
              <div v-if="closure.opening_balance_bs > 0" class="text-gray-500 text-[11px]">Bs. {{ formatCurrency(closure.opening_balance_bs) }}</div>
              <div v-if="closure.opening_balance_cop > 0" class="text-teal-700 text-[11px] font-semibold">COP {{ formatCurrency(closure.opening_balance_cop) }}</div>
            </td>

            <td class="p-3 text-xs text-center">
              <div class="font-bold text-gray-900">${{ formatCurrency(closure.expected_balance) }}</div>
              <div v-if="closure.expected_balance_bs > 0" class="text-gray-500 text-[11px]">Bs. {{ formatCurrency(closure.expected_balance_bs) }}</div>
              <div v-if="closure.expected_balance_cop > 0" class="text-teal-700 text-[11px] font-semibold">COP {{ formatCurrency(closure.expected_balance_cop) }}</div>
            </td>

            <td class="p-3 text-xs text-center">
              <div class="font-bold text-green-700">${{ formatCurrency(closure.closing_balance) }}</div>
              <div v-if="closure.closing_balance_bs > 0" class="text-gray-500 text-[11px]">Bs. {{ formatCurrency(closure.closing_balance_bs) }}</div>
              <div v-if="closure.closing_balance_cop > 0" class="text-teal-700 text-[11px] font-semibold">COP {{ formatCurrency(closure.closing_balance_cop) }}</div>
            </td>

            <td class="p-3 text-xs text-center">
              <div :class="closure.difference >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'">
                ${{ formatCurrency(closure.difference) }}
              </div>
              <div v-if="closure.difference_bs != 0" :class="closure.difference_bs >= 0 ? 'text-green-600 text-[11px]' : 'text-red-600 text-[11px]'">
                Bs. {{ formatCurrency(closure.difference_bs) }}
              </div>
              <div v-if="closure.difference_cop != 0" :class="closure.difference_cop >= 0 ? 'text-green-600 text-[11px] font-semibold' : 'text-red-600 text-[11px] font-semibold'">
                COP {{ formatCurrency(closure.difference_cop) }}
              </div>
            </td>

            <td class="p-3 text-xs font-semibold text-gray-800">
              {{ closure.closedBy?.name || (closure.closedBy ? (closure.closedBy.first_name + ' ' + (closure.closedBy.last_name || '')) : (closure.closed_by?.name || 'N/A')) }}
            </td>

            <td class="p-3">
              <div class="flex items-center justify-center space-x-2">
                <Link
                  :href="`/cash-closures/${closure.id}`"
                  class="text-blue-600 hover:text-blue-900"
                  title="Ver detalles"
                >
                  <i class="fa fa-eye"></i>
                </Link>

                <a
                  :href="`/cash-closures/${closure.id}/report`"
                  class="text-green-600 hover:text-green-900"
                  title="Ver reporte PDF"
                  target="_blank"
                >
                  <i class="fa fa-file-pdf"></i>
                </a>
              </div>
            </td>
          </tr>

          <tr v-if="!closures.data || closures.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="8">No hay cierres de caja.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      v-if="closures?.links || closures?.meta?.links"
      :links="closures.links || closures.meta.links"
      class="mt-6"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'CashClosuresIndex',

  components: {
    Head,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    closures: {
      type: Object,
      required: true
    },
    cashRegisters: {
      type: Array,
      default: () => []
    },
    organizations: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      filters: {
        cash_register_id: (this.$page.props.filters && this.$page.props.filters.cash_register_id) || '',
        organization_id: (this.$page.props.filters && this.$page.props.filters.organization_id) || '',
        date_from: (this.$page.props.filters && this.$page.props.filters.date_from) || '',
        date_to: (this.$page.props.filters && this.$page.props.filters.date_to) || '',
      },
    }
  },

  watch: {
    filters: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/cash-closures',
          pickBy(this.filters),
          { preserveState: true }
        )
      }, 300),
    },
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    formatCurrency(value, currency = 'USD') {
      const currencies = {
        'USD': 'en-US',
        'VES': 'es-VE',
        'COP': 'es-CO'
      }

      const symbols = {
        'USD': 'USD',
        'VES': 'VES',
        'COP': 'COP'
      }

      return new Intl.NumberFormat(currencies[currency] || 'en-US', {
        style: 'currency',
        currency: symbols[currency] || 'USD'
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
  }
}
</script>